import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const LINES = ["AI employees", "for small", "businesses."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const blobScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen pt-28 md:pt-32 overflow-hidden">
      {/* Parallax decorative blob */}
      <motion.div
        aria-hidden="true"
        style={{ y: blobY, scale: blobScale }}
        className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-brand-apricot/50 blur-[80px]"
      />
      <motion.div
        aria-hidden="true"
        style={{ y: blobY }}
        className="pointer-events-none absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-brand-sage/40 blur-[90px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-brand-teal mb-8 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-brand-teal" /> AI agents for small business
        </motion.div>

        <h1 className="font-display font-normal text-brand-ink text-[3.4rem] leading-[0.92] sm:text-7xl lg:text-[8.5rem] tracking-tight">
          {LINES.map((l, i) => (
            <span key={i} className="reveal-mask">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 1 ? (
                  <>
                    for <em className="italic text-brand-teal">small</em>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="md:col-span-5 text-lg md:text-xl leading-relaxed text-brand-ink/80"
          >
            We design and build custom AI agents that answer customers, qualify leads, and handle busywork — so small teams do more without hiring.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="md:col-span-4 md:col-start-9 flex flex-col gap-4"
          >
            <a
              href="#book"
              data-testid="hero-book-call"
              className="group inline-flex items-center justify-between rounded-full bg-brand-teal text-brand-cream pl-6 pr-2 py-2 font-mono text-sm uppercase tracking-[0.15em] hover:bg-brand-ink transition-colors duration-300"
            >
              Book a call
              <span className="ml-4 w-10 h-10 rounded-full bg-brand-cream/20 grid place-items-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight size={18} />
              </span>
            </a>
            <a
              href="#case-studies"
              data-testid="hero-view-work"
              className="text-center rounded-full border border-brand-teal text-brand-teal py-3 font-mono text-sm uppercase tracking-[0.15em] hover:bg-brand-peach transition-colors duration-300"
            >
              See our work
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
